
import React from 'react';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Star, StarHalf } from 'lucide-react';
import { Link } from 'react-router-dom';

interface TestimonialCardProps {
  quote: string;
  author: string;
  position: string;
  company: string;
  rating?: number;
  serviceLink?: string;
}

const TestimonialCard: React.FC<TestimonialCardProps> = ({ 
  quote, 
  author, 
  position, 
  company,
  rating = 5, // Default to 5 stars if not specified
  serviceLink
}) => {
  // Create star rating display
  const renderStars = () => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    // Add full stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={`star-${i}`} className="w-4 h-4 fill-yellow-400 text-yellow-400" />);
    }
    
    // Add half star if needed
    if (hasHalfStar) {
      stars.push(<StarHalf key="half-star" className="w-4 h-4 fill-yellow-400 text-yellow-400" />);
    }
    
    return stars;
  };

  return (
    <Card className="h-full bg-white shadow-md hover:shadow-lg transition-shadow duration-300">
      <CardContent className="pt-6">
        <div className="flex items-center mb-2">
          {rating ? (
            <div className="flex items-center" aria-label={`${rating} out of 5 stars`}>
              {renderStars()}
              <span className="ml-2 text-sm text-gray-600">{rating.toFixed(1)}</span>
            </div>
          ) : null}
        </div>
        <div className="text-4xl text-itmgmt-medium-blue mb-4">"</div>
        <p className="text-itmgmt-dark-gray mb-4 italic">{quote}</p>
      </CardContent>
      <CardFooter className="flex flex-col items-start border-t pt-4">
        <p className="font-bold text-itmgmt-dark-blue">{author}</p>
        <p className="text-sm text-gray-600">
          {position}{company && `, ${company}`}
        </p>
        {serviceLink && (
          <Link to={serviceLink} className="mt-2 text-itmgmt-bright-blue hover:underline text-sm">
            View related service
          </Link>
        )}
      </CardFooter>
    </Card>
  );
};

export default TestimonialCard;
